'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { useParams } from 'next/navigation';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { detectCityFromCoordinates } from '@/lib/fast-location-utils';
import { useAuth } from '@/providers/auth-provider';
import { useAiFoodOrderStore } from '@/store';

interface StoreCitySheetContextType {
  showCityPopup: boolean;
  isNavigating: boolean;
  openCitySheet: () => void;
  closeCitySheet: () => void;
  setNavigating: (navigating: boolean) => void;
  address: Address | null;
  setAddress: (address: Address | null) => void;
  checkCityMismatch: () => boolean;
}

const StoreCitySheetContext = createContext<
  StoreCitySheetContextType | undefined
>(undefined);

export function StoreCitySheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const params = useParams();

  // Get loading state from auth provider to check if URL params are being processed
  const { isLoading } = useAuth();

  // Use Zustand store instead of local state
  const storeAddress = useAiFoodOrderStore.use.address();
  const setStoreAddress = useAiFoodOrderStore.use.setAddress();
  const requireAddressSync = useAiFoodOrderStore.use.requireAddressSync();
  const setRequireAddressSync = useAiFoodOrderStore.use.setRequireAddressSync();

  // Delay initial check to avoid triggering on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second after mount

    return () => clearTimeout(timer);
  }, []);

  // Function to check if current URL city matches address city
  const checkCityMismatch = useCallback((): boolean => {
    if (!storeAddress || !params.city) {
      return false;
    }

    // Require coordinates — text-based matching is unreliable (EP may label
    // Tam Kỳ addresses with "Đà Nẵng" region text, causing false mismatches)
    if (!storeAddress.lat || !storeAddress.lng) {
      return false;
    }

    const currentUrlCity = params.city as string;
    const addressMatchedCity = detectCityFromCoordinates(
      storeAddress.lat,
      storeAddress.lng
    );

    if (addressMatchedCity && addressMatchedCity.slug !== currentUrlCity) {
      console.log(
        `🚨 City mismatch detected: URL=${currentUrlCity}, Address=${addressMatchedCity.slug}`
      );
      return true;
    }

    return false;
  }, [storeAddress, params.city]);

  // Auto-check for city mismatch and open sheet if needed
  useEffect(() => {
    // PRIORITY 1: Skip if URL params are being processed (window flag)
    if (
      typeof window !== 'undefined' &&
      (window as any).__processingUrlLocation
    ) {
      console.log(
        '⏭️ Skipping city mismatch check - URL processing in progress'
      );
      return;
    }

    // PRIORITY 2: Check for URL params to avoid conflicts with auth-provider URL handling
    const searchParams = new URLSearchParams(window.location.search);
    const hasUrlParams = searchParams.has('lat') && searchParams.has('lng');

    // PRIORITY 3: Check all other conditions before opening sheet
    if (
      !isInitialLoad &&
      !isNavigating && // Don't check if currently navigating
      !showCityPopup && // Don't open if sheet is already open
      !isLoading && // Don't open sheet while auth-provider is loading (processing URL params, login, etc.)
      !hasUrlParams && // Don't check city mismatch if URL params are being processed
      checkCityMismatch()
    ) {
      console.log('🔄 Opening city sheet due to city mismatch');
      setShowCityPopup(true);
    }
  }, [
    storeAddress,
    params.city,
    isNavigating, // Re-check when navigation state changes
    isInitialLoad,
    showCityPopup,
    isLoading, // Add isLoading as dependency to react to auth state changes
    checkCityMismatch,
  ]);

  // Auto-check for requireAddressSync flag and open sheet if needed
  useEffect(() => {
    if (
      !isInitialLoad &&
      !isNavigating &&
      !showCityPopup && // Don't open if sheet is already open
      !isLoading && // Don't open sheet while auth-provider is loading
      requireAddressSync
    ) {
      console.log('🔄 Opening city sheet due to requireAddressSync flag');
      setShowCityPopup(true);
      // Reset the flag once we've acted on it
      setRequireAddressSync(false);
    }
  }, [
    requireAddressSync,
    isInitialLoad,
    isNavigating,
    showCityPopup,
    isLoading, // Add isLoading as dependency
    setRequireAddressSync,
  ]);

  const openCitySheet = useCallback(() => {
    // Don't open sheet if auth-provider is loading (processing URL params, login, etc.)
    if (!isNavigating && !isLoading) {
      console.log('🏠 Opening city sheet manually');
      setShowCityPopup(true);
    } else if (isLoading) {
      console.log('🏠 Skipping city sheet open - auth provider is loading');
    }
  }, [isNavigating, isLoading]);

  const closeCitySheet = useCallback(() => {
    setShowCityPopup(false);
  }, []);

  const setNavigating = useCallback((navigating: boolean) => {
    setIsNavigating(navigating);
    if (navigating) {
      setShowCityPopup(false); // Close sheet when navigating
    }
  }, []);

  const value = {
    showCityPopup,
    isNavigating,
    openCitySheet,
    closeCitySheet,
    setNavigating,
    address: storeAddress,
    setAddress: setStoreAddress,
    checkCityMismatch,
  };

  return (
    <StoreCitySheetContext.Provider value={value}>
      {children}
    </StoreCitySheetContext.Provider>
  );
}

export function useStoreCitySheet() {
  const context = useContext(StoreCitySheetContext);
  if (context === undefined) {
    throw new Error(
      'useStoreCitySheet must be used within a StoreCitySheetProvider'
    );
  }
  return context;
}
